import * as React from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { PulseLoader } from "react-spinners"; // Importing the loader
import login from "../../Assets/Images/cashier.jpg";
import { jwtDecode } from "jwt-decode";
import { loginCallApi } from "../../data/userData";
import Swal from "sweetalert2";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

/**
 * Safely turns whatever the backend/axios throws at us into a readable
 * string. This is what fixes the "[object Object]" bug: previously
 * `response.response.data` (or similar) was passed straight into Swal's
 * `text` field. If that value is an object rather than a string, React/JS
 * stringifies it as "[object Object]" instead of showing anything useful.
 *
 * Handles, in order:
 *  - plain strings
 *  - { message: "..." }
 *  - { error: "..." }
 *  - { errors: [...] } or { errors: { field: "msg" } }  (validation errors)
 *  - arrays of strings/objects
 *  - anything else -> a generic fallback (never JSON.stringify raw to the
 *    user, since that's just as unreadable as "[object Object]")
 */
function getErrorMessage(payload, fallback = "Something went wrong. Please try again.") {
  if (!payload) return fallback;

  if (typeof payload === "string") {
    return payload.trim() || fallback;
  }

  if (Array.isArray(payload)) {
    const parts = payload
      .map((item) => getErrorMessage(item, null))
      .filter(Boolean);
    return parts.length ? parts.join("\n") : fallback;
  }

  if (typeof payload === "object") {
    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message;
    }
    if (typeof payload.error === "string" && payload.error.trim()) {
      return payload.error;
    }
    if (payload.errors) {
      if (Array.isArray(payload.errors)) {
        return getErrorMessage(payload.errors, fallback);
      }
      if (typeof payload.errors === "object") {
        const parts = Object.values(payload.errors)
          .flat()
          .map((v) => (typeof v === "string" ? v : getErrorMessage(v, null)))
          .filter(Boolean);
        if (parts.length) return parts.join("\n");
      }
    }
    if (typeof payload.title === "string" && payload.title.trim()) {
      return payload.title;
    }
  }

  return fallback;
}

/**
 * Pulls a usable message out of an axios-style error/response object,
 * whether it's the "response" object returned by loginCallApi on a
 * non-2xx status, or a thrown JS/axios error from a network failure.
 */
function extractErrorMessage(errorOrResponse) {
  // Thrown axios error: has .response.data
  if (errorOrResponse?.response?.data) {
    return getErrorMessage(errorOrResponse.response.data);
  }
  // Response-shaped object passed back directly (as this app does)
  if (errorOrResponse?.data) {
    return getErrorMessage(errorOrResponse.data);
  }
  // Plain Error instance
  if (errorOrResponse instanceof Error && errorOrResponse.message) {
    return errorOrResponse.message;
  }
  return getErrorMessage(errorOrResponse);
}

function saveTokenToStorage(decodedToken) {
  localStorage.setItem("decodedToken", JSON.stringify(decodedToken));
}

async function saveDataToLocalStorage(response) {
  const token = response.data.token;
  localStorage.setItem("token", JSON.stringify(token));

  const rawLogoPath = response.data.companyLogoPath;
  const filePath = rawLogoPath
    ? `/assets/CompanyLogos/${encodeURIComponent(rawLogoPath.split("/").pop())}`
    : null;
  localStorage.setItem("filePath", filePath);
  localStorage.setItem("saleSettings", JSON.stringify(response.data.salesettings));
  const expirationTime = new Date().getTime() + 5 * 60 * 60 * 1000;
  localStorage.setItem("tokenExpiration", expirationTime);
  localStorage.setItem("rights", JSON.stringify(response.data.rights));
  const decodedToken = jwtDecode(token);
  saveTokenToStorage(decodedToken);
}

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {"Copyright © "}
      <Link color="inherit" href="https://mcb.co.ke/">
        MCB Technologies
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

// TODO remove, this demo shouldn't need to reset the theme.
const defaultTheme = createTheme();

export default function SignInSide({ onLogin }) {
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [rememberMe, setRememberMe] = React.useState(false);

  React.useEffect(() => {
    sessionStorage.removeItem("password"); // clean old data
    const savedUsername = sessionStorage.getItem("username");
    if (savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }
  }, []);

  const handleUsernameChange = (e) => setUsername(e.target.value);
  const handlePasswordChange = (e) => setPassword(e.target.value);
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (event) => event.preventDefault();

  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);

  const showError = (title, payload) => {
    Swal.fire({
      icon: "error",
      title,
      text: getErrorMessage(payload),
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!username.trim() || !password) {
      showError("Missing Information", "Please enter both your email and password.");
      return;
    }

    setLoading(true);

    try {
      const response = await loginCallApi(username, password);

      if (response === "ERR_NETWORK") {
        showError(
          "Network Error!",
          "Unable to login, check your internet connection and try again."
        );
        return;
      }

      if (
        response?.code === "ERR_BAD_RESPONSE" ||
        response?.code === "ERR_BAD_REQUEST"
      ) {
        if (response?.response?.data) {
          showError("Invalid Credentials!", response.response.data);
        } else {
          showError("Server Error!", "Server error, contact Admin");
        }
        return;
      }

      if (response?.status === 200) {
        if (response.data.changepassword === true) {
          saveDataToLocalStorage(response);
          navigate("/changepassword");
          return;
        }

        if (rememberMe) {
          sessionStorage.setItem("username", username);
        } else {
          sessionStorage.removeItem("username");
        }

        saveDataToLocalStorage(response);

        try {
          const rights = response.data.rights;
          const cleanedData = JSON.parse(rights.replace(/\\/g, ""));
          localStorage.setItem("rightsforrbac", cleanedData);
        } catch (parseErr) {
          console.error("Failed to parse rights payload:", parseErr);
          // Non-fatal: continue login, but let the user know permissions
          // may not be fully loaded.
          Swal.fire({
            icon: "warning",
            title: "Partial Data",
            text: "Logged in, but some permission data couldn't be loaded. Some features may be limited.",
          });
        }

        const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
        const roleGroupId = parseInt(decodedToken.RoleGroupId);

        const isCompany = parseInt(decodedToken?.isCompany || 0);
        const isRegion = parseInt(decodedToken?.isRegion || 0);
        const isAdmin = parseInt(decodedToken?.isAdmin || 0);
        const RoleGroup = decodedToken?.RoleGroup || "";

        if (isCompany === 1 || isRegion === 1 || isAdmin === 1) {
          onLogin();
          navigate("/MCB-SWITCH");
        } else if (RoleGroup === "Branch") {
          onLogin();
          navigate("/butcheryPOS");
        } else {
          onLogin();
          navigate("/super-admin-dashboard");
        }
        return;
      }

      // Any other unexpected shape/status we didn't explicitly handle above.
      showError("Login Failed", response);
    } catch (error) {
      // Catches thrown network/axios errors, JSON parsing errors, etc.,
      // that previously only went to console.error with no user feedback.
      console.error("Login error:", error);
      showError("Login Failed", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 9999,
          }}
        >
          <PulseLoader size={15} color={"#3f51b5"} />
        </Box>
      )}
      <Grid container component="main" sx={{ height: "100vh" }}>
        <CssBaseline />
        <Grid
          item
          xs={false}
          sm={4}
          md={7}
          sx={{
            position: "relative",
            backgroundImage: `url(${login})`,
            backgroundRepeat: "no-repeat",
            backgroundColor: (t) =>
              t.palette.mode === "light"
                ? t.palette.grey[50]
                : t.palette.grey[900],
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
          <Box
            sx={{
              my: 8,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              Sign in
            </Typography>
            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit}
              sx={{ mt: 1 }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={username}
                onChange={handleUsernameChange}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="HashedPassword"
                label="Password"
                type={showPassword ? "text" : "password"}
                id="HashedPassword"
                autoComplete="current-password"
                value={password}
                onChange={handlePasswordChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    color="primary"
                  />
                }
                label="Remember me"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                Sign In
              </Button>
              <Grid container>
                <Grid item xs>
                  <Link href="/forgot-password" variant="body2">
                    Forgot password?
                  </Link>
                </Grid>
                <Grid item />
              </Grid>
              <Copyright sx={{ mt: 5 }} />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}